import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ImportDialog } from "@/components/admin/ImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Tags } from "lucide-react";

const BLANK = { name: "", code: "", description: "", active: true };

export function ProjectTypesCard() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // {type_id?, ...fields}

  const load = () => api.get("/admin/project-types").then((r) => setTypes(r.data.project_types || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const name = (edit.name || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    const payload = { name, code: edit.code || "", description: edit.description || "", active: edit.active !== false };
    try {
      if (edit.type_id) await api.put(`/admin/project-types/${edit.type_id}`, payload);
      else await api.post("/admin/project-types", payload);
      toast.success(edit.type_id ? "Project type updated" : "Project type added");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/admin/project-types/${t.type_id}`, { name: t.name, code: t.code || "", description: t.description || "", active: !t.active });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete project type "${t.name}"?`)) return;
    try { await api.delete(`/admin/project-types/${t.type_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= types.length) return;
    const arr = [...types];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setTypes(arr);
    try { await api.post("/admin/project-types/reorder", { ordered_ids: arr.map((t) => t.type_id) }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="project-types-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-900 text-white grid place-items-center"><Tags className="h-4 w-4" /></div>
          <div>
            <h3 className="font-bold text-slate-900">Project Types</h3>
            <p className="text-sm text-slate-500 mt-0.5 max-w-xl">Workspace taxonomy used in the project form. Only active types are selectable; historical values are preserved.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ImportDialog entity="project-types" label="Project Types" onImported={load}
            paths={{ template: "/admin/project-types/template", preview: "/admin/project-types/import/preview", commit: "/admin/project-types/import/commit" }} />
          <Button onClick={() => setEdit({ ...BLANK })} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="project-type-add-btn"><Plus className="h-4 w-4 mr-2" />Add type</Button>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="w-24">Order</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-28">Code</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-24">In use</TableHead>
              <TableHead className="w-24">Active</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Loading…</TableCell></TableRow>
            ) : types.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500" data-testid="project-types-empty">No project types yet. Add one or import a file.</TableCell></TableRow>
            ) : types.map((t, i) => (
              <TableRow key={t.type_id} data-testid={`project-type-row-${t.type_id}`}>
                <TableCell>
                  <div className="flex items-center gap-0.5">
                    <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`project-type-up-${t.type_id}`}><ChevronUp className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === types.length - 1} onClick={() => move(i, 1)} data-testid={`project-type-down-${t.type_id}`}><ChevronDown className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
                <TableCell className="font-medium text-slate-900">{t.name}</TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{t.code || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600 max-w-[260px] truncate">{t.description || "—"}</TableCell>
                <TableCell>{t.in_use ? <Badge variant="outline" className="bg-slate-50">{t.in_use}</Badge> : <span className="text-slate-400 text-sm">0</span>}</TableCell>
                <TableCell><Switch checked={t.active} onCheckedChange={() => toggleActive(t)} data-testid={`project-type-active-${t.type_id}`} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEdit({ ...t })} data-testid={`project-type-edit-${t.type_id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => remove(t)} data-testid={`project-type-delete-${t.type_id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="project-type-dialog">
          <DialogHeader><DialogTitle>{edit?.type_id ? "Edit project type" : "Add project type"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Name</Label>
              <Input value={edit?.name || ""} onChange={(e) => setEdit((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Window Display" className="mt-1" data-testid="project-type-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Code (optional)</Label>
                <Input value={edit?.code || ""} onChange={(e) => setEdit((s) => ({ ...s, code: e.target.value }))} placeholder="e.g. WIN" className="mt-1" data-testid="project-type-code-input" />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch checked={edit?.active !== false} onCheckedChange={(c) => setEdit((s) => ({ ...s, active: c }))} data-testid="project-type-active-switch" />
                <span className="text-sm text-slate-600">Active</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Description (optional)</Label>
              <Textarea value={edit?.description || ""} onChange={(e) => setEdit((s) => ({ ...s, description: e.target.value }))} rows={2} className="mt-1" data-testid="project-type-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            <Button className="bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={save} data-testid="project-type-save-btn">{edit?.type_id ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
